import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

/**
 * Mobile-only sticky contact bar.
 *
 * - Renders only on viewports ≤768px (visually hidden via Tailwind's
 *   `min-[769px]:hidden`).
 * - Two warm, soft-shadowed actions: "Bel ons" (tel:) + "Contact" (/contact).
 * - Auto-hides when the footer comes into view (or when the mobile menu
 *   is open) so it never sits on top of the user's destination.
 * - Honors `env(safe-area-inset-bottom)` for iPhone home-bar clearance.
 * - When mounted, reserves `--mobile-cta-h` of bottom padding on <main>
 *   so page content never sits behind the bar.
 */
const PHONE_HREF = "tel:+32476796175";

const MobileStickyCta = () => {
  const [visible, setVisible] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  // On camp-related pages, the primary action is registration, not contact.
  const isCampContext =
    pathname.startsWith("/zomerkampen") || pathname.startsWith("/inschrijven");

  // Hide when footer (or any element marked data-hide-mobile-cta) is in view.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 768px)").matches) return;

    const targets = document.querySelectorAll<HTMLElement>(
      "footer, [data-hide-mobile-cta]"
    );
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        // If any sentinel is intersecting, hide the bar.
        const anyVisible = entries.some((e) => e.isIntersecting);
        setVisible(!anyVisible);
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.01 }
    );

    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  // Hide when the mobile drawer is open (drawer adds `inert` to <main>).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const main = document.querySelector("main");
    if (!main) return;
    const mo = new MutationObserver(() => {
      if (main.hasAttribute("inert")) setVisible(false);
    });
    mo.observe(main, { attributes: true, attributeFilter: ["inert"] });
    return () => mo.disconnect();
  }, []);

  // Publish the bar's measured height as a CSS custom property so layout
  // can reserve space (we set bottom padding on <main> from Layout).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const publish = () => {
      const h = visible ? el.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty(
        "--mobile-cta-h",
        `${Math.round(h)}px`
      );
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", publish);
      document.documentElement.style.removeProperty("--mobile-cta-h");
    };
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={ref}
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 0.68, 0.35, 1] }}
          className="fixed inset-x-0 bottom-0 z-[80] min-[769px]:hidden pointer-events-none"
          aria-label="Snel contact"
          role="navigation"
        >
          <div
            className="pointer-events-auto mx-auto w-full max-w-[640px] px-4"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
              paddingTop: "10px",
            }}
          >
            <div
              className="flex items-center gap-2.5 rounded-full border border-border/40 bg-background/92 px-2 py-2 backdrop-blur-xl"
              style={{
                boxShadow:
                  "0 1px 2px hsl(25 20% 18% / 0.06), 0 8px 28px -8px hsl(25 20% 18% / 0.18), 0 16px 48px -20px hsl(32 35% 52% / 0.10)",
              }}
            >
              <a
                href={PHONE_HREF}
                className="flex flex-1 min-h-[48px] items-center justify-center gap-2 rounded-full border border-border/50 bg-secondary/70 px-4 text-[14.5px] font-semibold text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Bel Praktijk Sprankelien op +32 476 79 61 75"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                <span>Bel ons</span>
              </a>
              {isCampContext ? (
                <Link
                  to="/inschrijven"
                  className="flex flex-1 min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary px-4 text-[14.5px] font-semibold text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Schrijf je kind in voor het zomerkamp"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  <span>Inschrijven</span>
                </Link>
              ) : (
                <Link
                  to="/contact"
                  className="flex flex-1 min-h-[48px] items-center justify-center gap-2 rounded-full bg-primary px-4 text-[14.5px] font-semibold text-primary-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Ga naar de contactpagina"
                >
                  <MessageCircle className="h-4 w-4" aria-hidden="true" />
                  <span>Contact</span>
                </Link>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileStickyCta;
